/**
 * 技能文档管理页面
 *
 * 显示和管理用户的技能文档，包括：
 * - 查看所有技能文档
 * - 查看技能详情
 * - 删除技能文档
 *
 * 可选功能，用于增强用户对系统的了解和控制。
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AuthorCenterLayout } from '../components/layout/AuthorCenterLayout';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import memoryApi from '../services/memoryApi';
import type { SkillDocument } from '../types/memory';

export const SkillDocumentPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, initialized } = useAuthStore();

  const [skills, setSkills] = useState<SkillDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSkill, setSelectedSkill] = useState<SkillDocument | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // 等待初始化完成
  useEffect(() => {
    if (initialized && !isAuthenticated) {
      navigate('/login');
    }
  }, [initialized, isAuthenticated, navigate]);

  // 加载技能文档
  const fetchSkills = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await memoryApi.getSkillDocuments();
      setSkills(data.skills);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取技能文档失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSkills();
    }
  }, [fetchSkills, isAuthenticated]);

  // 删除技能文档
  const handleDelete = async (skillId: number) => {
    if (!window.confirm('确定要删除这个技能文档吗？')) {
      return;
    }

    setDeletingId(skillId);
    try {
      await memoryApi.deleteSkillDocument(skillId);
      setSkills((prev) => prev.filter((s) => s.id !== skillId));
      if (selectedSkill?.id === skillId) {
        setSelectedSkill(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败');
    } finally {
      setDeletingId(null);
    }
  };

  // 按使用次数排序
  const sortedSkills = [...skills].sort((a, b) => b.usage_count - a.usage_count);

  // 等待初始化
  if (!initialized) {
    return (
      <AuthorCenterLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
        </div>
      </AuthorCenterLayout>
    );
  }

  // 未登录
  if (!isAuthenticated) {
    return null;
  }

  return (
    <AuthorCenterLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* 标题和返回 */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">技能文档</h1>
              <p className="text-gray-500 mt-1">
                系统自动从你的对话中学习到的可复用技能
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/memory')}
                className="text-gray-500 hover:text-gray-700"
              >
                查看记忆
              </button>
              <button
                onClick={() => navigate('/profile')}
                className="text-gray-500 hover:text-gray-700"
              >
                返回个人资料
              </button>
            </div>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
              {error}
              <button
                onClick={() => setError('')}
                className="ml-4 underline"
              >
                关闭
              </button>
            </div>
          )}

          {/* 统计 */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {skills.length}
                </div>
                <div className="text-sm text-gray-500">技能文档总数</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {skills.reduce((sum, s) => sum + s.usage_count, 0)}
                </div>
                <div className="text-sm text-gray-500">总使用次数</div>
              </div>
            </div>
          </div>

          {/* 技能列表 */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-paper-500"></div>
            </div>
          ) : skills.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="mt-2">暂无技能文档</p>
              <p className="text-sm mt-1">
                与 AI 进行复杂的多步骤任务时，系统会自动提取可复用的技能
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sortedSkills.map((skill) => (
                <div
                  key={skill.id}
                  className={`bg-white rounded-lg border p-4 cursor-pointer transition-colors ${
                    selectedSkill?.id === skill.id
                      ? 'border-paper-500 ring-1 ring-paper-500'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedSkill(skill)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{skill.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        使用 {skill.usage_count} 次
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(skill.id);
                        }}
                        disabled={deletingId === skill.id}
                        className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                        title="删除"
                      >
                        {deletingId === skill.id ? (
                          <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full"></div>
                        ) : (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {skill.description}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <span>{skill.steps.length} 个步骤</span>
                    <span>•</span>
                    <span>
                      创建于 {new Date(skill.created_at).toLocaleDateString('zh-CN')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 选中技能详情 */}
          {selectedSkill && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {selectedSkill.name}
                      </h2>
                      <p className="text-gray-500 mt-1">
                        使用 {selectedSkill.usage_count} 次 • {selectedSkill.steps.length} 个步骤
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedSkill(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">描述</h3>
                    <p className="text-gray-900">{selectedSkill.description}</p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">执行步骤</h3>
                    <ol className="space-y-3">
                      {selectedSkill.steps.map((step, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-paper-100 text-paper-700 rounded-full flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </span>
                          <p className="text-gray-900 pt-0.5">{step}</p>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      创建于 {new Date(selectedSkill.created_at).toLocaleString('zh-CN')}
                    </span>
                    <button
                      onClick={() => {
                        handleDelete(selectedSkill.id);
                      }}
                      disabled={deletingId === selectedSkill.id}
                      className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                    >
                      删除技能
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 说明 */}
          <div className="mt-8 p-4 bg-purple-50 rounded-lg text-sm text-purple-800">
            <h3 className="font-medium mb-2">关于技能文档</h3>
            <ul className="space-y-1 text-purple-700">
              <li>• 技能文档是从你的对话中自动提取的可复用流程</li>
              <li>• 只有满足"可复用、有深度、多步骤"标准的流程才会被记录</li>
              <li>• 技能会在后续对话中帮助 AI 更高效地完成类似任务</li>
              <li>• 你可以删除不需要的技能文档</li>
            </ul>
          </div>
        </div>
      </div>
    </AuthorCenterLayout>
  );
};

export default SkillDocumentPage;
